import { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Switch,
  Radio,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { colors, roleColors } from "../ui/colors";
import { renderIconResidential } from "../services/helpers/renderIconResidential";
import { renderIconCommercial } from "../services/helpers/renderIconCommercial";

export const JobFormServices = ({
  title,
  data = [],
  switchState,
  storeAction,
  sx,
  multiselect = true,
  type = "commercial", // "residential" o "commercial"
}) => {
  const [switchOptions, setSwitchOptions] = useState(switchState || []);
  const dispatch = useDispatch();

  const palette =
    type === "residential" ? roleColors.residential : roleColors.commercial;

  const renderIcon = (name, active, size = 22) => {
    return type === "residential"
      ? renderIconResidential(name, active, size)
      : renderIconCommercial(name, active, size);
  };

  const handleChange = (storeElement, value) => {
    let updatedOptions;

    if (multiselect) {
      updatedOptions = value
        ? [
          ...switchOptions,
          {
            ...storeElement,
            workServiceId: storeElement.id,
            staffHours: [{ type: "", quantity: "", hoursPerStaff: "" }],
            squareFt: 0,
            supplies: [],
          },
        ]
        : switchOptions.filter((item) => item?.id !== storeElement?.id);
    } else {
      if (value) {
        updatedOptions = [
          {
            ...storeElement,
            workServiceId: storeElement.id,
            staffHours: [{ type: "", quantity: "", hoursPerStaff: "" }],
            squareFt: 0,
            supplies: [],
          },
        ];
      } else {
        updatedOptions = [];
      }
    }

    setSwitchOptions(updatedOptions);
    dispatch(storeAction(updatedOptions));
  };


  const isChecked = (item) =>
    Array.isArray(switchOptions) &&
    switchOptions.some((option) => option.id === item.id);

  const sortedData = [...data].sort((a, b) =>
    (a?.workServices?.name || "").localeCompare(b?.workServices?.name || "")
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        p: { xs: 1, sm: 2 },
        ...sx,
      }}
    >
      {/* Header */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: palette.base,
          fontSize: { xs: "0.95rem", sm: "1rem" },
        }}
      >
        {title}
      </Typography>

      {/* Empty state */}
      {!data || data.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            color: "text.secondary",
            fontStyle: "italic",
            textAlign: "center",
          }}
        >
          {`There are no active ${type} services. Activate them in `}
          <strong>Organization &gt; Manage Services</strong>.
        </Typography>
      ) : (
        <Grid
          container
          sx={{
            flex: 1,
            width: "100%",
            mx: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          {sortedData.map((storeElement, index) => {
            const active = isChecked(storeElement);

            return (
              <Card
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  border: `1px solid ${active ? palette[200] : colors.gray[200]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  minHeight: 72,
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: palette[200],
                  },
                }}
              >
                {/* Icon + Name */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: active ? palette.base : colors.gray[400],
                    }}
                  >
                    {renderIcon(storeElement.workServices?.name, active, 22)}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 600 : 500,
                      color: active ? colors.gray[800] : colors.gray[600],
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      flex: 1,
                    }}
                    title={storeElement.workServices?.name}
                  >
                    {storeElement.workServices?.name || "Unnamed"}
                  </Typography>
                </Box>

                {/* Switch o Radio */}
                {multiselect ? (
                  <Switch
                    size="small"
                    checked={active}
                    onChange={(e) => handleChange(storeElement, e.target.checked)}
                    sx={{
                      "& .MuiSwitch-track": {
                        bgcolor: active
                          ? `${palette[200]} !important`
                          : `${colors.gray[300]} !important`,
                      },
                      "& .MuiSwitch-thumb": {
                        bgcolor: active ? palette.base : colors.gray[200],
                      },
                    }}
                  />
                ) : (
                  <Radio
                    checked={active}
                    onChange={() => handleChange(storeElement, true)}
                    sx={{
                      "&.Mui-checked": {
                        color: palette.base,
                      },
                    }}
                  />
                )}
              </Card>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};
